<template>
    <v-dialog :value="value" max-width="760px" persistent scrollable @input="$emit('input', $event)">
      <v-card>
  
        <!-- card header -->
        <v-card-title>
          <span class="headline">{{ editMode ? "تعديل سبب رفض" : "إضافة سبب رفض" }}</span>
        </v-card-title>
  
        <!-- card body -->
        <v-card-text>
          <v-progress-linear v-if="fetching" indeterminate color="primary" class="mb-4" rounded />
  
          <!-- ref="form" mean that we can call form function using this.$refs.form. -->
           <!-- v-model="valid" means that the form is valid when all validation rules pass. -->
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="form.title"
                  label=" سبب الرفض"
                  :rules="[rules.required]"
                  :error-messages="fieldErrors.title"
                  outlined
                  dense
                />
              </v-col>

              <v-col cols="12">
                <div class="field-label mb-2">نوع المنتج</div>
                <v-autocomplete
                  v-model="form.wsata_product_type_id"
                  :items="wsataProductTypeOptions"
                  :loading="loadingWsataProductType"
                  item-text="name"
                  item-value="id"
                  label="اختر نوع المنتج"
                  :rules="[rules.requiredSelect]"
                  :error-messages="fieldErrors.wsata_product_type_id"
                  outlined
                  dense
                />
              </v-col>        
  
              <v-col cols="12">
                <div class="field-label mb-2">المستخدم</div>
                <v-autocomplete
                  v-model="form.user_role"
                  :items="userRoleOptions"
                  :loading="loadingUserRole"
                  item-text="name"
                  item-value="id"
                  label="اختر المستخدم"
                  :rules="[rules.requiredSelect]"
                  :error-messages="fieldErrors.user_role"
                  outlined
                  dense
                />
              </v-col>
  
            </v-row>
          </v-form>
        </v-card-text>
  
        <!-- card footer -->
        <v-card-actions>
          <!-- Pushes buttons to the right in the footer. -->
          <v-spacer />
          <v-btn text @click="close">إلغاء</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="fetching" @click="submit">
            {{ editMode ? "حفظ التعديلات" : "إضافة" }}
          </v-btn>
        </v-card-actions>
  
      </v-card>
    </v-dialog>
  </template>
  
  <script>
  import Axios from "axios";
  import { axiosInstance } from "@/axios/config";
  
  const emptyForm = () => ({
    title: "",
    wsata_product_type_id: null,
    user_role: null,
  });

  const emptyFieldErrors = () => ({
    title: [],
    wsata_product_type_id: [],
    user_role: [],
  });
  
  export default {
    name: "RejectionReasonForm",
    // props are the properties that are passed to the component from the parent component.
    props: {
      value: { type: Boolean, default: false },
      editId: { type: [Number, String], default: null },
    },
    data() {
      return {
        valid: false,
        fetching: false,
        saving: false,
        loadingWsataProductType: false,
        loadingUserRole: false,
        form: emptyForm(),
        fieldErrors: emptyFieldErrors(),
        wsataProductTypeOptions: [],
        userRoleOptions: [],
        rules: {
          required: (v) => !!String(v || "").trim() || "سبب الرفض مطلوب",
          requiredSelect: (v) =>
            (v !== null && v !== undefined && v !== "") || "هذا الحقل مطلوب",
        },
      };
    },
    computed: {
      editMode() {//true if editId is set, false otherwise
        return this.editId != null && this.editId !== "";
      },
      apiBase() {//the base URL for the API
        return `${this.$store.state.url}/employee/admin/rejections-reasons`;
      },
    },
    //Runs code when something changes:
    watch: {
      value(open) {//runs when the dialog is opened or closed
        if (open) {
          this.resetForm();
          this.initForm();
        }
      },
    },
    methods: {
      async initForm() {
        await Promise.all([
          this.loadWsataProductTypeOptions(),
          this.loadUserRoleOptions(),
        ]);
        if (this.editMode) {
          await this.loadReason();
        }
      },
      resetForm() {
        this.form = emptyForm();
        this.fieldErrors = emptyFieldErrors();
        this.$nextTick(() => {
          if (this.$refs.form) this.$refs.form.resetValidation();
        });
      },
      close() {
        this.$emit("input", false);
      },
      //=============================================================================
      //Loads the wsata product type options from the API
      async loadWsataProductTypeOptions() {
        this.loadingWsataProductType = true;
        try {
          const res = await Axios.get(`${this.apiBase}/meta/wsata-product-types`, {
            headers: this.request_headers(),
          });
          this.wsataProductTypeOptions = this.mapWsataProductTypeSelection(res.data.payload.data);
        } catch (e) {
          this.showPopup("error", e.response?.data?.message || "تعذر تحميل قائمة نوع المنتج");
        } finally {
          this.loadingWsataProductType = false;
        }
      },
      //Maps API product types to { id, name } for autocomplete
      mapWsataProductTypeSelection(list) {
        return (list || []).map((wsataProductType) => ({
          id: wsataProductType.id,
          name: wsataProductType.name.ar,
        }));
      },
      //=============================================================================
      //Loads the user role options from the API
      async loadUserRoleOptions() {
        this.loadingUserRole = true;
        try {
          await axiosInstance.get(`/employee/admin/announcements/allRoles`).then((res) => {
            this.userRoleOptions = this.mapUserRoleSelection(res.data.payload);
          });
        }
        catch (e) {
          this.showPopup("error", e.response?.data?.message || "تعذر تحميل قائمة المستخدم");
        }
        finally {
          this.loadingUserRole = false;
        }
      },
      //Maps API user roles to { id, name } for autocomplete
       mapUserRoleSelection(list) {
        return (list || []).map((userRole) => ({
          id: Number(userRole.value),
          name: userRole.name,
        }));
      },
      //=============================================================================
      async loadReason() {//loads the reason data from the API for editing
        this.fetching = true;
        try {
          const res = await Axios.get(`${this.apiBase}/${this.editId}`, {
            headers: this.request_headers(),
          });
          const item = res.data.payload || {};
          this.form = {
            title: item.title || "",
            wsata_product_type_id:
              item.wsata_product_type_id != null ? Number(item.wsata_product_type_id) : null,
            user_role: this.resolveUserRoleValue(item),
          };
        } catch (e) {
          this.showPopup("error", e.response?.data?.message || "تعذر تحميل البيانات");
          this.close();
        } finally {
          this.fetching = false;
        }
      },
      resolveUserRoleValue(item) {
        if (Number(item.is_customer_service) === 1 && Number(item.user_role) === 0) {
          return 15;
        }
        if (item.user_role === null || item.user_role === undefined || item.user_role === "") {
          return null;
        }
        return Number(item.user_role);
      },
      //=============================================================================
      buildPayload() {
        return {
          title: this.form.title.trim(),
          wsata_product_type_id: Number(this.form.wsata_product_type_id),
          user_role: Number(this.form.user_role),
        };
      },
      clearFieldErrors() {
        this.fieldErrors = emptyFieldErrors();
      },
      applyApiValidationErrors(errors = {}) {
        const next = emptyFieldErrors();
        Object.keys(next).forEach((field) => {
          if (errors[field]) {
            next[field] = Array.isArray(errors[field]) ? errors[field] : [errors[field]];
          }
        });
        this.fieldErrors = next;
      },
      firstValidationMessage(errors = {}) {
        const messages = Object.values(errors).flat().filter(Boolean);
        return messages[0] || null;
      },
      //=============================================================================
      async submit() {//submits the form data to the API
        if (!this.$refs.form.validate()) return;//if the form is not valid, return

        this.saving = true;
        this.clearFieldErrors();
        try {
          const payload = this.buildPayload();
          const headers = {
            ...this.request_headers(),
            "Content-Type": "application/json",
          };

          if (this.editMode) {//if we are editing, send a PUT request to the API
            await Axios.put(`${this.apiBase}/${this.editId}`, payload, { headers });
            this.showPopup("success", "تم تحديث سبب الرفض");
          } else {//if we are adding, send a POST request to the API
            await Axios.post(this.apiBase, payload, { headers });
            this.showPopup("success", "تم إضافة سبب الرفض");
          }
          /*
          Emit = “this child tells the parent something happened.”
  
          this.$emit('input', false);   // close dialog (works with v-model)
          this.$emit('saved');          // parent runs fetchItems()
          */
          this.$emit("saved");//emits the saved event to the parent component
          this.close();//closes the dialog
        } catch (e) {
          const apiErrors = e.response?.data?.errors;
          if (apiErrors) {
            this.applyApiValidationErrors(apiErrors);
            this.showPopup(
              "error",
              this.firstValidationMessage(apiErrors) || "يرجى تصحيح الحقول المطلوبة"
            );
          } else {
            this.showPopup("error", e.response?.data?.message || "تعذر الحفظ");
          }
        } finally {//finally block runs after the try or catch block, regardless of whether an error occurred
          this.saving = false;
        }
      },
      //=============================================================================
    },
  };
  </script>
  
  <!-- scoped means that the styles are only applied to this component -->
  <style scoped>
  .field-label {
    font-weight: 600;
    font-size: 0.9rem;
  }
      
  .theme--dark .field-label,
  .dark .field-label {
    color: rgba(255, 255, 255, 0.87);
  }
  </style>